<template>
	<div>
		<div
			v-if="!isEmpty(preference) && !isEmpty(emailTemplates)"
			class="settings-detail"
		>
			<NotificationBar
				v-if="disableContactLevelAlert || disableLoanLevelAlert"
				class="notification"
				:icon="['far', 'exclamation-circle']"
				:message="notificationMessage"
			/>
			<div class="alert-description">
				{{ getAlertDescription }}
			</div>

			<div v-if="isUnite && !disableLoanLevelAlert" class="unite">
				<div class="target-ltv">
					<FormInputs
						:name="uniteSettings.name"
						:label="uniteSettings.label"
						:type="uniteSettings.type"
						:value="uniteSettings.value"
						:formField="uniteSettings.formField"
						:placeholder="uniteSettings.placeholder"
						:max="uniteSettings.max"
						:min="uniteSettings.min"
						:step="uniteSettings.step"
						@handle-input="updateParameter('LTV', ...arguments)"
						:disabled="uniteSettings.disabled || mainProfileIsInternalUser"
					/>
				</div>
				<i class="fal fa-percent"></i>
			</div>
			<div v-if="isRateWatch && !disableLoanLevelAlert" class="rate-watch">
				<FormDropdowns
					class="target-rate-drop"
					label="Target Rate Drop"
					:options="rateDropOptions"
					:value="rateWatchSettings.targetComp.value"
					:disabled="mainProfileIsInternalUser"
					@change="updateParameter('RatePercentageIncrements', ...arguments)"
				/>
				<div class="target-comp">
					<CheckBox
						id="target-comp-checkbox"
						class="target-comp-checkbox"
						@onClick="onTargetCompChange"
						:disabled="mainProfileIsInternalUser"
						:checked="rateWatchSettings.targetAfterComp.checked"
					/>
					<label for="target-comp-checkbox" class="target-comp-label"
						>Target After Comp</label
					>
				</div>
				<div class="price">
					<label class="price-label">
						Please Select Price (%) or Price ($) to Set the Price
					</label>
					<div class="percent-container">
						<FormRadioButtons
							:radioGroup="[rateWatchSettings.price[0]]"
							@handleInput="onPercentChange(true)"
							:forClientConnect="true"
							:disabled="mainProfileIsInternalUser"
						/>
						<FormInputs
							name="percentPrice"
							class="percent-price"
							:disabled="!isPercent || mainProfileIsInternalUser"
							type="number"
							:step="0.01"
							:value="isPercent ? rateWatchSettings.ratePricing : 0"
							@handle-input="updateParameter('RatePricing', ...arguments)"
						/>
					</div>
					<div class="dollar-container">
						<FormRadioButtons
							:radioGroup="[rateWatchSettings.price[1]]"
							@handleInput="onPercentChange(false)"
							:forClientConnect="true"
							:disabled="mainProfileIsInternalUser"
						/>
						<FormInputs
							name="dollarPrice"
							class="dollar-price"
							type="number"
							:step="0.01"
							:disabled="isPercent || mainProfileIsInternalUser"
							:value="!isPercent ? rateWatchSettings.ratePricing : 0"
							@handle-input="updateParameter('RatePricing', ...arguments)"
						/>
					</div>
					<ul class="pricing-info">
						<li>This loan includes LLPA and the same product type is used.</li>
						<li v-if="rateWatchSettings.targetAfterComp.checked">
							You selected to cover your comp plan and
							<span v-show="!isPercent">${{ ratePricing }}</span>
							<span v-show="isPercent">{{ ratePricing }}%</span>
							amount of credit toward borrower cost.
						</li>
						<li v-else>
							You selected to cover
							<span v-show="!isPercent">${{ ratePricing }}</span>
							<span v-show="isPercent">{{ ratePricing }}%</span>
							amount of credit toward borrower cost.
						</li>
					</ul>
				</div>
			</div>
			<ContactCreditSuppression
				ref="contactCreditSuppression"
				v-if="isUconnect"
				@handleSuppressionClick="handleSuppressionClick"
				@clearSuppressionExpiredDate="clearSuppressionExpiredDate"
				:isAlertSuppressed="settings.isAlertSuppressed"
				:suppressAlertEndDate="settings.suppressAlertEndDate"
				:disabled="
					disableLoanLevelAlert ||
					disableContactLevelAlert ||
					mainProfileIsInternalUser ||
					settings.optInInd === false ||
					settings.optInInd === undefined
				"
			/>
			<FormRadioButtons
				:legendLabel="`${settings.type.desc} Watch`"
				:radioGroup="watchSettings"
				@handleInput="handleWatchSettingsChange"
				:forClientConnect="true"
				:disabled="
					disableLoanLevelAlert ||
					disableContactLevelAlert ||
					mainProfileIsInternalUser
				"
			/>
			<FormRadioButtons
				legendLabel="Borrowers Notifications Mode"
				:radioGroup="notificationModeSettings"
				@handleInput="handleNotificationsModeChange"
				:forClientConnect="true"
				v-if="settings.optInInd"
				:disabled="mainProfileIsInternalUser"
			/>
			<FormCheckboxButtons
				legendLabel="Borrowers Notifications"
				:checkboxGroup="notifications"
				:disabled="true"
				v-if="settings.optInInd && settings.automaticInd"
			/>
			<section v-if="settings.optInInd && settings.automaticInd">
				<div class="b360-col-12 b360-form-label" role="heading">
					Select Template for Borrower Email
				</div>
				<!--TODO: Reusing Preference center component here with few changes,
					We should probably move this component to Global and rename it -->
				<PreferenceCenterImageRadio
					v-for="(card, index) in this.emailTemplates"
					:key="index"
					:card="card"
					class="email-template b360-col-xs-12 b360-col-sm-12 b360-col-md-12"
					:showPreview="false"
					:alertName="`${settings.type.desc} Watch`"
					:isChecked="isTemplateChecked(index)"
					:disabled="mainProfileIsInternalUser"
					@clicked="handleTemplateClick"
				/>
			</section>
		</div>
		<LoadingIcon v-else alignment="center" />
		<OverviewModalFooter
			:success="success"
			:primaryButtonDisabled="mainProfileIsInternalUser"
			:waiting="waiting"
			:isSaveModal="true"
			@handleClick="handleSavePreferences"
			@close="handleClose"
			primaryButtonName="Save"
		/>
	</div>
</template>

<script>
import { isEmpty, cloneDeep } from 'lodash';
import { mapActions, mapState } from 'vuex';
import leadsService from '@/services/contacts/leads-service.js';
import loansService from '@/services/contacts/loans-service.js';

import CheckBox from '../CheckBox.vue';
import FormCheckboxButtons from '../../../components/global/FormCheckboxButtons.vue';
import FormDropdowns from '../../../components/global/FormDropdowns.vue';
import FormInputs from '../../../components/global/FormInputs.vue';
import FormRadioButtons from '../../../components/global/FormRadioButtons.vue';
import LoadingIcon from '../../../components/global/LoadingIcon.vue';
import NotificationBar from '../../../components/global/NotificationBar.vue';
import OverviewModalFooter from './OverviewModalFooter.vue';
import PreferenceCenterImageRadio from '../../../components/preference-center/PreferenceCenterImageRadio.vue';
import ContactCreditSuppression from './ContactCreditSuppression.vue';
import { getAlertTypeId } from '../helpers';
import { ALERT_DESCRIPTIONS } from './constants';
import { ALERT_TYPES } from '../constants';

export default {
	name: 'AlertSettings',
	components: {
		CheckBox,
		FormCheckboxButtons,
		FormDropdowns,
		FormInputs,
		FormRadioButtons,
		LoadingIcon,
		NotificationBar,
		OverviewModalFooter,
		PreferenceCenterImageRadio,
		ContactCreditSuppression,
	},
	props: {
		preference: Object,
		isUWMContact: Boolean,
		isUWMLoan: Boolean,
	},
	async created() {
		this.settings = cloneDeep(this.preference.alertPreference);
		this.leadId = this.preference.leadId;
		this.loanId = this.preference.loanId;
		if (this.isUnite) {
			this.uniteSettings.value = this.getUniteValue();
		}
		if (this.isRateWatch) {
			this.setRateWatchValues();
		}
		this.updateCheckedValues();
		if (isEmpty(this.rateDropOptions)) {
			await this.getRateDropOptions();
		}
		await this.getEmailTemplates();
		this.selectedTemplateIndex =
			this.settings.emailTemplate &&
			this.settings.emailTemplate.emailTemplateId === 0
				? 0
				: this.getSeletedTemplateIndex();

		/**
		 * set loanDetails view to 'settings' tab, back button navigation
		 * should take the user to the settings view
		 */
		this.setLoanDetailsView('Settings');
	},
	computed: {
		...mapState({
			alertTypes: (state) => state.clientConnectV2.alerts.types.data,
			rateDropOptions: (state) => state.preferenceCenterHome.rateDropOptions,
			mainProfileIsInternalUser: (state) =>
				state.profileSwitcher.mainProfileSelector.isInternalUser,
		}),
		getAlertDescription() {
			return ALERT_DESCRIPTIONS.find(
				(alert) => alert.name === this.settings.type.desc,
			).description;
		},
		disableContactLevelAlert() {
			return (
				(this.settings.type.desc === ALERT_TYPES.CREDIT ||
					this.settings.type.desc === ALERT_TYPES.UCONNECT) &&
				!this.isUWMContact
			);
		},
		disableLoanLevelAlert() {
			return (
				(
					this.settings.type.desc === ALERT_TYPES.RATE ||
					this.settings.type.desc === ALERT_TYPES.UNITE ||
					this.settings.type.desc === ALERT_TYPES.MI_DROP_ELIGIBLE ||
					this.settings.type.desc === ALERT_TYPES.NEW_LISTIN ||
					this.settings.type.desc === ALERT_TYPES.REVIEW_ME
				) && !this.isUWMLoan
			);
		},
		notificationMessage() {
			return this.disableContactLevelAlert
				? 'Sorry, this alert type is only available for contacts with UWM loans.'
				: 'Sorry, this alert type is only available for UWM loans.';
		},
		isCreditWatch() {
			return (
				this.settings.type.desc.toLowerCase() === 'credit' &&
				!isEmpty(this.settings.parameters)
			);
		},
		isUnite() {
			return this.settings.type.desc.toLowerCase() === 'unite';
		},
		isRateWatch() {
			return this.settings.type.desc.toLowerCase() === 'rate';
		},
		isPercent() {
			const isPercent = this.settings.parameters.find(
				(obj) => obj.preferenceParam === 'IsRatePricingPercent',
			);
			if (
				isPercent &&
				(isPercent.value === 'true' || isPercent.value === true)
			) {
				return true;
			}
			return false;
		},
		isUconnect() {
			return this.settings.type.desc.toLowerCase() === 'uconnect';
		},
		ratePricing() {
			const pricing = this.settings.parameters.find(
				(obj) => obj.preferenceParam === 'RatePricing',
			);
			return pricing && pricing.value;
		},
	},
	methods: {
		...mapActions([
			'getRateDropOptions',
			'setLoanDetailsView',
			'getEmailTemplatesByAlertTypeId',
		]),
		// deepcode ignore VueIncorrectMethodType: This is am imported function and the only way to use this function in the template
  		isEmpty,
		resetState() {
			if (this.success) {
				this.success = false;
			}
		},
		getSeletedTemplateIndex() {
			let selectedTemplate = this.emailTemplates.find(
				(template) =>
					template.emailTemplateId ===
					this.settings.emailTemplate.emailTemplateId,
			);
			if (!selectedTemplate) {
				selectedTemplate = this.emailTemplates[0];
			}
			return selectedTemplate.id;
		},
		isTemplateChecked(index) {
			return index === this.selectedTemplateIndex;
		},
		handleTemplateClick(index) {
			this.selectedTemplateIndex = parseInt(index, 10);
		},
		onTargetCompChange(selected) {
			this.updateParameter('IsTargetAfterComp', { value: selected });
			this.rateWatchSettings.targetAfterComp.checked = selected;
		},
		onPercentChange(isPercent) {
			this.updateParameter('IsRatePricingPercent', { value: isPercent });
			this.updateParameter('RatePricing', { value: 0 });
		},
		setRateWatchValues() {
			const comp = this.settings.parameters.find(
				(obj) => obj.preferenceParam === 'RatePercentageIncrements',
			);
			const targetAfterComp = this.settings.parameters.find(
				(obj) => obj.preferenceParam === 'IsTargetAfterComp',
			);
			this.rateWatchSettings.targetComp.value = comp.value;
			this.rateWatchSettings.targetAfterComp.checked =
				targetAfterComp.value === 'true';
			if (this.isPercent) {
				this.rateWatchSettings.price[0].checked = true;
			} else {
				this.rateWatchSettings.price[1].checked = true;
			}
			this.rateWatchSettings.ratePricing = this.getRatePricing();
		},
		handleSuppressionClick(isOn, endDate) {
			this.resetState();
			this.settings.isAlertSuppressed = isOn;
			this.settings.suppressAlertEndDate = endDate;
			// This causes ContactCreditSupresson prop values to update and in turn forces a rerender
			this.settings = cloneDeep(this.settings);
		},
		clearSuppressionData() {
			this.settings.isAlertSuppressed = false;
			this.settings.suppressAlertEndDate = '';
			this.settings = cloneDeep(this.settings);
		},
		async clearSuppressionExpiredDate(isOn, endDate) {
			/**
			 * This method fires before selectedTemplateIndex gets assigment.
			 * If it's still null we need to way a lifecycle to ensure all the lifecycle methods have run.
			 */
			if (this.selectedTemplateIndex === null) {
				await this.$nextTick();
			}
			this.resetState();
			this.settings.isAlertSuppressed = isOn;
			this.settings.suppressAlertEndDate = endDate;
			// This causes ContactCreditSupresson prop values to update and in turn forces a rerender
			await this.handleSavePreferences(true);
			this.settings = cloneDeep(this.settings);
		},
		handleWatchSettingsChange(event) {
			this.resetState();
			if (event.value === 'default') {
				this.settings.optInInd = null;
			} else if (event.value === 'true') {
				this.settings.optInInd = true;
			} else {
				this.settings.optInInd = false;
			}
			this.updateCheckedValues();
		},
		handleNotificationsModeChange(event) {
			this.resetState();
			if (event.value === 'true') {
				this.settings.automaticInd = true;
			} else {
				this.settings.automaticInd = false;
			}
			this.updateCheckedValues();
		},
		updateParameter(prop, event) {
			this.resetState();
			// eslint-disable-next-line no-restricted-syntax
			for (const parameter of this.settings.parameters) {
				if (parameter.preferenceParam === prop) {
					parameter.value = event.value;
				}
			}
			this.updateCheckedValues();
		},
		handleClose() {
			this.$emit('close');
		},
		updateCheckedValues() {
			// Watch settings
			switch (true) {
				case this.settings.optInInd === true:
					this.watchSettings[0].checked = true;
					break;
				case this.settings.optInInd === false:
					this.watchSettings[1].checked = true;
					break;
				case this.settings.optInInd == null:
					this.watchSettings[2].checked = true;
					break;
				default:
			}

			// Notification mode
			switch (true) {
				case this.settings.automaticInd === true:
					this.notificationModeSettings[0].checked = true;
					break;
				case this.settings.automaticInd === false:
					this.notificationModeSettings[1].checked = true;
					break;
				default:
			}
		},
		getRatePricing() {
			const ratePricing = this.settings.parameters.find(
				(obj) => obj.preferenceParam === 'RatePricing',
			);
			return ratePricing && ratePricing.value;
		},
		getUniteValue() {
			const ltv = this.settings.parameters.find(
				(obj) => obj.preferenceParam === 'LTV',
			);
			return ltv && ltv.value;
		},
		async getEmailTemplates() {
			const response = await this.getEmailTemplatesByAlertTypeId(
				this.settings.type.id,
			);
			this.emailTemplates = response.map((template, index) => ({
				name: `${this.settings.type.desc}-email-template`,
				label: 'Email Template Image',
				value: template.templateCode,
				id: index,
				link: template.thumbnailURL,
				emailTemplateId: template.emailTemplateId,
			}));
		},
		async handleSavePreferences(isAutomated = false) {
			if (!isAutomated) {
				this.waiting = true;
			}
			const typeId = getAlertTypeId(this.alertTypes, this.settings.type.desc);
			const { emailTemplateId } = this.emailTemplates.find(
				(template) => template.id === this.selectedTemplateIndex,
			);
			try {
				if (this.loanId) {
					const payload = {
						loanId: this.loanId,
						preferences: [
							{
								typeId,
								emailTemplateId,
								optInInd: this.settings.optInInd,
								automaticInd: this.settings.automaticInd,
								parameters: this.settings.parameters,
							},
						],
					};
					await loansService.saveLoanPreferences(payload);
				} else if (this.leadId) {
					// if alerts are disabled clear out supression data befor sending the payload
					if (this.settings.optInInd === false) {
						this.clearSuppressionData();
						// Refresh dom
						this.settings = cloneDeep(this.settings);
					}
					let payload = {
						leadId: this.leadId,
						preferences: [
							{
								typeId,
								emailTemplateId,
								optInInd: this.settings.optInInd,
								automaticInd: this.settings.automaticInd,
								parameters: this.settings.parameters,
								isAlertSuppressed: this.settings.isAlertSuppressed,
								suppressAlertEndDate: this.settings.suppressAlertEndDate,
							},
						],
					};
					await leadsService.saveLeadPreferences(payload);
				}
				if (!isAutomated) {
					setTimeout(() => {
						this.waiting = false;
						this.success = true;
					}, 250);
				}
			} catch (error) {
				this.error =
					"We're unable to save your preferences. Please try again in few minutes.";
				this.waiting = false;
			}
		},
	},
	data() {
		return {
			watchSettings: [
				{
					name: `${this.preference.alertPreference.type.desc}`,
					labelPrefix: 'Enabled:',
					label: 'Show alerts in Client Connect',
					value: true,
					id: 0,
					section: 'watchSettings',
					checked: false,
				},
				{
					name: `${this.preference.alertPreference.type.desc}`,
					labelPrefix: 'Disabled:',
					label: 'Do not show alerts in Client Connect',
					value: false,
					id: 1,
					section: 'watchSettings',
					checked: false,
				},
				{
					name: `${this.preference.alertPreference.type.desc}`,
					labelPrefix: 'Global Default:',
					label: 'Use the Preference Center alert settings',
					value: 'default',
					id: 2,
					section: 'watchSettings',
					checked: false,
				},
			],
			notificationModeSettings: [
				{
					name: `${this.preference.alertPreference.type.desc}-notifications-mode`,
					labelPrefix: 'Automatic:',
					label:
						'We will automatically reach out to your borrowers on your behalf',
					value: true,
					id: 0,
					section: 'notificationModeSettings',
					checked: false,
				},
				{
					name: `${this.preference.alertPreference.type.desc}-notifications-mode`,
					labelPrefix: 'Manual:',
					label:
						'You have the ability to contact your borrower at your own convenience.',
					value: false,
					id: 1,
					section: 'notificationModeSettings',
					checked: false,
				},
			],
			notifications: [
				{
					name: `${this.preference.alertPreference.type.desc}-borrowers-notifications`,
					labelPrefix: 'Email:',
					label: 'We will automatically email your borrower and copy you.',
					value: true,
					id: 0,
					section: 'notifications',
					checked: true,
				},
			],
			uniteSettings: {
				name: 'target-ltv',
				label: 'Target LTV',
				type: 'number',
				placeholder: '',
				pattern: '\\d{0,3}[.]\\d{0,2}',
				required: false,
				formField: '80',
				value: null,
				disabled: false,
				section: 'un',
				max: 150,
				min: 0,
				step: 0.01,
			},
			rateWatchSettings: {
				targetComp: {
					value: null,
				},
				targetAfterComp: {
					checked: null,
				},
				ratePricing: null,
				isRatePercent: null,
				price: [
					{
						name: 'rateWatchPricing',
						label: 'Price (%)',
						value: 0,
						id: 0,
						section: 'rateWatchPricing',
						checked: false,
					},
					{
						name: 'rateWatchPricing',
						label: 'Price ($)',
						value: 1,
						id: 1,
						section: 'rateWatchPricing',
						checked: false,
					},
				],
			},
			settings: {},
			emailTemplates: [],
			selectedTemplateIndex: null,
			leadId: null,
			loanId: null,
			success: false,
			waiting: false,
			error: null,
		};
	},
};
</script>

<style lang="scss" scoped>
.settings-detail {
	@include rem('padding', 20px);
	@include rem('max-height', 480px);
	overflow-y: auto;

	/deep/ fieldset {
		border-bottom: none;
		@include rem('margin-bottom', 5px);
	}

	/deep/ input[type='checkbox']:checked + label {
		&.disabled {
			&::before {
				background-color: $brand-blue;
			}
		}
	}
}

.settings-detail-fieldset {
	display: block;
	margin: 0;
	padding: 0;
	border: none;
}

.unite {
	display: flex;
	align-items: center;
	@include rem('margin-bottom', 20px);

	.target-ltv {
		width: 50%;
	}

	.fa-percent {
		@include rem('margin', 30px 0 0 5px);
	}
}

.target-comp {
	/deep/ .custom-checkbox {
		top: 0;
	}
}

/deep/ .b360-form-input--select {
	width: 50%;
}

.percent-container,
.dollar-container {
	@include rem('margin-top', 20px);
}

.percent-price,
.dollar-price {
	width: 50%;
}

.pricing-info {
	@include rem('margin', 20px 0);
	@include rem('padding-left', 15px);
}

.target-rate-drop,
.target-comp,
.price-label,
.alert-description,
.notification {
	@include rem('margin-bottom', 20px);
}

.target-comp-label {
	@include rem('margin-left', 30px);
}

.label-prefix {
	font-weight: 500;
}
</style>
